import { ClientApp, clear_websocket, set_websocket } from "target_wasm";
import { createLogger } from "debug/DebugingLogger";
import { axiosInstance } from "contexts/axiosApiInstance";
import { handlePaste } from "./canvasEventHandlers/PasteEventHandler";
import { useBoundStore } from "@/components/canvas/stores/useAppStore";
import { showNotificationGlobal } from "stores/useNotificationStore";

export type DisconnectReason =
  | "activity"
  | "duplicate"
  | "error"
  | "closed"
  | "system_failure"
  | "write_too_large";

const log = createLogger("Canvas", "Actions");
import { AxiosError } from "axios";

export async function fetchTableDeletionStates(modelId: string): Promise<void> {
  try {
    const { data } = await axiosInstance.get<
      Record<string, [boolean, boolean]>
    >(`/action/model/${modelId}/table-deletion-states`);

    const store = useBoundStore.getState();
    store.resetTableDeletionStates();

    Object.entries(data).forEach(([tableId, state]) => {
      if (Array.isArray(state) && state.length === 2) {
        store.setTableDeletionState(tableId, state[0], state[1]);
      }
    });
  } catch (error) {
    const err = error as AxiosError;
    log.error(`[fetchTableDeletionStates] API Error:`, {
      status: err.response?.status,
      data: err.response?.data,
    });
  }
}

interface WebSocketConfig {
  clientId: string;
  clientApp: ClientApp;
  modelId: string;
  onSocketChange: (socket: WebSocket | null) => void;
  onConnected: () => void;
  onDisconnected: (reason: DisconnectReason) => void;
}

const createWebsocket = (config: WebSocketConfig) => {
  const {
    clientId,
    clientApp,
    modelId,
    onSocketChange,
    onConnected,
    onDisconnected,
  } = config;

  const WEBSOCKET_URL = String(import.meta.env.VITE_APP_WEBSOCKET_URL);
  const sessionId = String(clientApp.get_session_id());

  let activeSocket: WebSocket | null = null;
  let socketOpened = false;
  let isCleanedUp = false;

  if (!navigator.onLine) {
    log.info("Network offline. Skipping WebSocket connection.");
    return () => {};
  }

  try {
    const url = `${WEBSOCKET_URL}${modelId}/${clientId}/${sessionId}`;
    log.info("Creating WebSocket for client_id: %s", clientId);
    const socket = new WebSocket(url);
    socket.binaryType = "arraybuffer";
    activeSocket = socket;

    socket.onopen = () => {
      if (isCleanedUp) {
        socket.close();
        return;
      }
      socketOpened = true;
      log.debug("WebSocket connection opened");
      clientApp.initialize_state();
      onConnected();
    };

    socket.onmessage = (event) => {
      if (isCleanedUp) return;
      try {
        clientApp.handle_ws_message(event);
      } catch (error) {
        log.error("Error handling WebSocket message:", error);
      }
    };

    socket.onclose = (event) => {
      if (isCleanedUp) return;

      log.debug("WebSocket connection closed", {
        code: event.code,
        reason: event.reason,
      });

      clear_websocket();
      onSocketChange(null);

      // Determine disconnect reason
      if (event.reason?.includes("write_too_large")) {
        log.warning("WebSocket closed: write exceeded the size limit");
        // The edge refused the change in flight; the reconnect would send it again.
        try {
          clientApp.discard_action_in_flight();
        } catch (error) {
          log.error("Discarding the refused change failed:", error);
        }
        showNotificationGlobal(
          "That edit was too large to save and was discarded.",
          "error",
        );
        onDisconnected("write_too_large");
      } else if (event.reason?.includes("multiplayer_actor_stopped")) {
        log.info("WebSocket closed due to server error");
        onDisconnected("system_failure");
      } else if (event.code === 1000 && event.reason?.includes("activity")) {
        onDisconnected("activity");
      } else if (
        event.reason?.includes("Duplicate session") ||
        event.code === 1008 ||
        (!socketOpened &&
          event.code === 1006 &&
          event.reason?.includes("Duplicate"))
      ) {
        log.warning("WebSocket closed due to duplicate session");
        onDisconnected("duplicate");
      } else if (event.code !== 1000) {
        onDisconnected("error");
      } else {
        onDisconnected("closed");
      }
    };

    socket.onerror = (event) => {
      if (isCleanedUp) return;
      log.error("WebSocket error:", event);
    };

    onSocketChange(socket);
    set_websocket(socket);
    clientApp.flush_actions();
  } catch (error) {
    log.error("Error during WebSocket connection:", error);
    onDisconnected("error");
  }

  return () => {
    isCleanedUp = true;
    if (activeSocket) {
      // Don't null out event handlers — messages may still arrive between
      // cleanup and socket.close(). The isCleanedUp flag guards the handlers.
      if (activeSocket.readyState === 0 || activeSocket.readyState === 1) {
        log.info("Forcing WebSocket close.");
        activeSocket.close(1000, "leave");
      }
      clear_websocket();
      activeSocket = null;
    }
    onSocketChange(null);
  };
};

const fetchModelScheduledDeletion = async (
  modelId: string,
  setModelScheduledDeletionDate: (date: number | null) => void,
) => {
  try {
    const response = await axiosInstance.get(`/action/model/${modelId}`);
    setModelScheduledDeletionDate(
      response.data.scheduled_deletion_date ?? null,
    );
  } catch (error) {
    log.error("Error fetching model:", error);
  }
};

const handle_paste = async (
  event: ClipboardEvent,
  clientApp: ClientApp,
  worker: Worker,
  modelID?: string,
) => {
  if (clientApp && worker) {
    await handlePaste(event, clientApp, worker, modelID);
  }
};
const downloadTableFile = (
  blob: Blob,
  tableName: string,
  extension: string = "csv",
) => {
  if (!blob) {
    log.error("downloadTableFile:", "Blob is null or undefined");
    return;
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${tableName}.${extension}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export {
  createWebsocket,
  fetchModelScheduledDeletion,
  handle_paste,
  downloadTableFile,
};
